import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../lib/api';

function buildSummaryFromPositions(positions) {
  const summary = {
    totalPositions: positions.length,
    totalValue: 0,
    totalUnrealizedPnL: 0,
    totalRealizedPnL: 0,
    byStatus: {
      open: 0,
      closed: 0,
      stopped_out: 0,
      take_profit: 0
    }
  };

  for (const p of positions) {
    const status = p.status || 'open';
    if (summary.byStatus[status] === undefined) {
      summary.byStatus[status] = 0;
    }
    summary.byStatus[status] += 1;

    summary.totalValue += p.currentValue || 0;
    summary.totalUnrealizedPnL += p.unrealizedPnL || 0;
    summary.totalRealizedPnL += p.realizedPnL || 0;
  }

  return summary;
}

function tradeKey(trade) {
  if (!trade) return '';
  const id = trade._id || trade.id || trade.orderId || trade.transactionHash || trade.txHash;
  if (id) return id.toString();
  const marketId = trade?.market?.conditionId || trade?.market?.slug || '';
  const side = trade?.side || '';
  const filledAt = trade?.filledAt || trade?.createdAt || '';
  const size = trade?.filledSize || trade?.size || '';
  const price = trade?.avgFillPrice || trade?.price || '';
  return `${marketId}:${side}:${filledAt}:${size}:${price}`;
}

export const usePaperPositionStore = create(persist(
  (set, get) => ({
    positions: [],
    trades: [],
    summary: null,
    isLoading: false,
    error: null,

    fetchPositions: async (status = 'open') => {
      try {
        set({ isLoading: true, error: null });
        const response = await api.get('/paper/positions', { params: { status } });
        const positions = response.data || [];
        set({ positions, summary: buildSummaryFromPositions(positions), isLoading: false });
      } catch (error) {
        set({ error: error.response?.data?.error || 'Failed to fetch paper positions', isLoading: false });
      }
    },

    fetchSummary: async () => {
      try {
        const response = await api.get('/paper/positions/summary');
        set({ summary: response.data });
      } catch (error) {
        console.error('Failed to fetch paper summary:', error);
      }
    },

    fetchTrades: async (params = {}) => {
      try {
        set({ isLoading: true, error: null });
        const response = await api.get('/paper/trades', { params });
        set({ trades: response.data, isLoading: false });
      } catch (error) {
        set({ error: error.response?.data?.error || 'Failed to fetch paper trades', isLoading: false });
      }
    },

    updateStopLoss: async (positionId, stopLossData) => {
      try {
        const response = await api.put(`/paper/positions/${positionId}/stop-loss`, stopLossData);
        set({
          positions: get().positions.map(p => p._id === positionId ? response.data : p)
        });
        return { success: true };
      } catch (error) {
        return { success: false, error: error.response?.data?.error };
      }
    },

    updateTakeProfit: async (positionId, takeProfitData) => {
      try {
        const response = await api.put(`/paper/positions/${positionId}/take-profit`, takeProfitData);
        set({
          positions: get().positions.map(p => p._id === positionId ? response.data : p)
        });
        return { success: true };
      } catch (error) {
        return { success: false, error: error.response?.data?.error };
      }
    },

    closePosition: async (positionId) => {
      try {
        console.log('📝 Closing paper position:', positionId);
        const response = await api.post(`/paper/positions/${positionId}/close`);
        console.log('📝 Close response:', response.data);
        const closedPosition = response.data?.position;
        if (!closedPosition) {
          console.error('📝 No position data in response');
          return { success: false, error: 'No position data returned' };
        }
        const updatedPositions = get().positions.filter(p => p._id !== positionId);
        console.log('📝 Updated positions count:', updatedPositions.length);
        set({
          positions: updatedPositions,
          summary: buildSummaryFromPositions(updatedPositions)
        });
        if (response.data?.trade) {
          get().addTrade(response.data.trade);
        }
        return { success: true, data: response.data };
      } catch (error) {
        console.error('Close paper position error:', error);
        return { success: false, error: error.response?.data?.error || error.message || 'Failed to close position' };
      }
    },

    updatePosition: (position) => {
      const prev = get().positions;
      const idx = prev.findIndex(p => p._id === position._id);
      const updatedPositions = idx >= 0
        ? prev.map(p => p._id === position._id ? position : p)
        : [position, ...prev];
      set({
        positions: updatedPositions,
        summary: buildSummaryFromPositions(updatedPositions)
      });
    },

    addTrade: (trade) => {
      const key = tradeKey(trade);
      const prev = get().trades || [];
      const next = key
        ? [trade, ...prev.filter((t) => tradeKey(t) !== key)]
        : [trade, ...prev];
      set({ trades: next.slice(0, 300) });
    },

    clearError: () => {
      set({ error: null });
    }
  }),
  {
    name: 'betmimic.paperPositionStore',
    partialize: (state) => ({
      positions: state.positions,
      trades: state.trades,
      summary: state.summary
    })
  }
));
